import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Calendar,
  Users,
  Clock,
  MessageSquare,
  Copy,
  FileText,
  CheckCircle2,
  Download,
  Lightbulb,
  Folder,
  FolderOpen,
  ChevronDown,
  BarChart3,
  X,
  SmilePlus,
  TrendingUp,
  Star,
  Send,
} from "lucide-react";
import webAuthService from "../services/webAuthService";
import apiService from "../services/apiService";

const MeetingDetails = ({ meetingId, onBack }) => {
  const navigate = useNavigate();
  const [chunks, setChunks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState("");
  const [actionItems, setActionItems] = useState("");
  const [groups, setGroups] = useState([]);
  const [currentGroupId, setCurrentGroupId] = useState(null);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [updatingGroup, setUpdatingGroup] = useState(false);
  const [userId, setUserId] = useState(null);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [evaluationData, setEvaluationData] = useState(null);
  const [evaluationType, setEvaluationType] = useState("sales");
  const [loadingEvaluation, setLoadingEvaluation] = useState(false);
  const [evaluationError, setEvaluationError] = useState(null);
  const [showSentimentModal, setShowSentimentModal] = useState(false);
  const [sentimentData, setSentimentData] = useState(null);
  const [loadingSentiment, setLoadingSentiment] = useState(false);
  const [sentimentError, setSentimentError] = useState(null);

  // Suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [feedbackStates, setFeedbackStates] = useState({});
  const [submittingFeedback, setSubmittingFeedback] = useState({});

  useEffect(() => {
    const initUser = async () => {
      const uid = await webAuthService.getUserId();
      setUserId(uid);
    };
    initUser();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showGroupDropdown &&
        !event.target.closest(".group-selector-container")
      ) {
        setShowGroupDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showGroupDropdown]);

  useEffect(() => {
    if (meetingId && userId) {
      fetchMeetingChunks();
      loadGroups();
      fetchMeetingDetails();
      fetchSuggestions(); // Fetch suggestions
    }
  }, [meetingId, userId]);

  const loadGroups = async () => {
    try {
      const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8080";
      const response = await fetch(`${baseURL}/groups?user_id=${userId}`);
      const data = await response.json();

      if (data.status === "success") {
        setGroups(data.groups || []);
      }
    } catch (error) {
      console.error("Failed to load groups:", error);
    }
  };

  const fetchMeetingDetails = async () => {
    try {
      const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8080";
      const response = await fetch(`${baseURL}/check_meeting/${meetingId}`);
      const data = await response.json();

      if (data.status === "success" && data.meeting) {
        setCurrentGroupId(data.meeting.group_id || null);
      }
    } catch (error) {
      console.error("Failed to fetch meeting details:", error);
    }
  };

  const handleUpdateMeetingGroup = async (groupId) => {
    try {
      setUpdatingGroup(true);
      const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8080";

      const response = await fetch(`${baseURL}/update_meeting_obj`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          meeting_id: meetingId,
          updates: {
            group_id: groupId || null,
          },
        }),
      });

      const data = await response.json();

      if (data.status === "success") {
        setCurrentGroupId(groupId);
        setShowGroupDropdown(false);

        const groupName = groupId
          ? groups.find((g) => g.id === groupId)?.name || "group"
          : "None";

      } else {
        throw new Error(data.message || "Failed to update meeting group");
      }
    } catch (error) {
      console.error("Failed to update meeting group:", error);
      alert("Failed to update meeting group");
    } finally {
      setUpdatingGroup(false);
    }
  };

  const fetchMeetingChunks = async () => {
    try {
      setLoading(true);
      setError(null);

      const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8080";
      const response = await fetch(
        `${baseURL}/get_meeting_chunks/${meetingId}`,
        {
          method: "GET",
          headers: {
            accept: "text/plain",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch chunks: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === "success") {
        setChunks(data.chunks || []);
        setSummary(data.summary || "");
        setActionItems(data.action_items || "");
      } else {
        throw new Error("Failed to fetch meeting chunks");
      }
    } catch (err) {
      console.error("Error fetching meeting chunks:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fetch suggestions for the meeting
  const fetchSuggestions = async () => {
    if (!meetingId) return;

    setLoadingSuggestions(true);
    try {
      const response = await apiService.get(
        `/feedback/meeting/${meetingId}/suggestions`,
      );
      if (response.success) {
        setSuggestions(response.suggestions || []);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Submit feedback for a suggestion
  const submitFeedback = async (suggestionId, rating, feedbackText) => {
    try {
      setSubmittingFeedback((prev) => ({ ...prev, [suggestionId]: true }));

      const data = await apiService.submitSuggestionFeedback(
        suggestionId,
        rating,
        feedbackText,
      );

      // Update feedback state
      setFeedbackStates((prev) => ({
        ...prev,
        [suggestionId]: {
          ...prev[suggestionId],
          submitted: true,
        },
      }));

      // Update the suggestion in the list
      setSuggestions((prev) =>
        prev.map((s) =>
          s.id === suggestionId
            ? {
                ...s,
                rating,
                feedback: feedbackText,
                feedback_timestamp: new Date().toISOString(),
              }
            : s,
        ),
      );

      // Hide feedback box after 2 seconds
      setTimeout(() => {
        setFeedbackStates((prev) => ({
          ...prev,
          [suggestionId]: {
            ...prev[suggestionId],
            showFeedbackBox: false,
          },
        }));
      }, 2000);
    } catch (error) {
      console.error("❌ Error submitting feedback:", error);
      alert("Failed to submit feedback. Please try again.");
    } finally {
      setSubmittingFeedback((prev) => ({ ...prev, [suggestionId]: false }));
    }
  };

  // Handle star rating click
  const handleStarClick = (suggestionId, rating) => {
    setFeedbackStates((prev) => ({
      ...prev,
      [suggestionId]: {
        ...prev[suggestionId],
        rating,
        showFeedbackBox: true,
      },
    }));
  };

  // Handle feedback text change
  const handleFeedbackChange = (suggestionId, text) => {
    setFeedbackStates((prev) => ({
      ...prev,
      [suggestionId]: {
        ...prev[suggestionId],
        feedback: text,
      },
    }));
  };

  const getUniqueParticipants = (chunks) => {
    const participants = new Set();
    chunks.forEach((chunk) => {
      const speakers = extractSpeakers(chunk.chunk_text);
      speakers.forEach((speaker) => participants.add(speaker));
    });
    return participants.size;
  };

  const formatChunkText = (text) => {
    return text.replace(/<PAD>/g, "").trim();
  };

  const extractSpeakers = (text) => {
    // Match speaker patterns like "Guest-1:", "Smit-1:", etc.
    const speakerMatches = text.match(/\b([A-Za-z]+-\d+):/g);
    if (speakerMatches) {
      return [...new Set(speakerMatches)].map((speaker) =>
        speaker.replace(":", ""),
      );
    }
    return [];
  };

  const copyToClipboard = (text, label) => {
    const cleanText = text.replace(/<[^>]*>/g, "");
    navigator.clipboard
      .writeText(cleanText)
      .then(() => {
        alert(`${label} copied to clipboard!`);
      })
      .catch((error) => {
        alert("Failed to copy to clipboard");
        console.error("Failed to copy:", error);
      });
  };

  const fetchEvaluation = async () => {
    if (!meetingId || !userId) {
      setEvaluationError("Meeting ID or User ID not available");
      return;
    }

    setLoadingEvaluation(true);
    setEvaluationError(null);

    try {
      const response = await apiService.get(
        `/meetings/evaluation?user_id=${userId}&meeting_id=${meetingId}`,
      );

      const evaluationData = {
        sales_evaluation: response.sales_evaluation || {},
        client_evaluation: response.client_evaluation || {},
      };

      setEvaluationData(evaluationData);
      setShowEvaluationModal(true);
    } catch (error) {
      console.error("Error fetching evaluation:", error);
      setEvaluationError(error.message || "Failed to fetch evaluation data");
    } finally {
      setLoadingEvaluation(false);
    }
  };

  const fetchSentiment = async () => {
    if (!meetingId || !userId) {
      setSentimentError("Meeting ID or User ID not available");
      return;
    }

    setLoadingSentiment(true);
    setSentimentError(null);

    try {
      const response = await apiService.get(
        `/meetings/sentiment?user_id=${userId}&meeting_id=${meetingId}`,
      );

      // Extract sentiment data from response (it's nested under "sentiment" key)
      const sentimentData = response.sentiment || response;

      setSentimentData(sentimentData);
      setShowSentimentModal(true);
    } catch (error) {
      console.error("Error fetching sentiment:", error);
      setSentimentError(error.message || "Failed to fetch sentiment data");
    } finally {
      setLoadingSentiment(false);
    }
  };

  // Helper to safely get numeric value
  const getNumericValue = (value) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const renderScoreBar = (score, maxScore = 10) => {
    const numericScore = getNumericValue(score);
    const percentage = (numericScore / maxScore) * 100;

    // Gradient color based on percentage for dark theme
    let gradientColor;
    if (percentage >= 70) {
      gradientColor = "linear-gradient(90deg, #10b981 0%, #34d399 100%)"; // Green gradient
    } else if (percentage >= 40) {
      gradientColor = "linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)"; // Blue gradient
    } else {
      gradientColor = "linear-gradient(90deg, #8b5cf6 0%, #a78bfa 100%)"; // Purple gradient
    }

    return (
      <div style={{ width: "100%", marginTop: "8px" }}>
        <div
          style={{
            height: "10px",
            backgroundColor: "rgba(51, 65, 85, 0.5)", // Dark slate background
            borderRadius: "6px",
            overflow: "hidden",
            border: "1px solid rgba(100, 116, 139, 0.3)",
          }}
        >
          <div
            style={{
              width: `${percentage}%`,
              height: "100%",
              background: gradientColor,
              transition: "width 0.3s ease",
              boxShadow: "0 0 10px rgba(59, 130, 246, 0.3)",
            }}
          />
        </div>
        <span
          style={{
            fontSize: "13px",
            color: "rgb(148, 163, 184)",
            marginTop: "6px",
            display: "block",
            fontWeight: "500",
          }}
        >
          {numericScore} / {maxScore}
        </span>
      </div>
    );
  };

  const renderSalesEvaluation = (data) => {
    if (!data) return null;

    const isNumeric = (val) =>
      typeof val === "number" || !isNaN(parseFloat(val));

    return (
      <div className="evaluation-content">
        {data["Overall Salesperson Performance Score"] !== undefined && (
          <div className="eval-section">
            <h3 className="eval-section-title">
              Overall Salesperson Performance Score:{" "}
              {data["Overall Salesperson Performance Score"]}/10
            </h3>
            {renderScoreBar(data["Overall Salesperson Performance Score"], 10)}
          </div>
        )}

        {Object.entries(data)
          .filter(([key]) => key !== "Overall Salesperson Performance Score")
          .map(([sectionKey, sectionData]) => {
            if (!sectionData || typeof sectionData !== "object") return null;

            return (
              <div key={sectionKey} className="eval-section">
                <h3 className="eval-section-title">{sectionKey}</h3>
                {Object.entries(sectionData).map(([key, value]) => (
                  <div key={key} className="eval-metric">
                    <h4 className="eval-subsection-title">{key}</h4>
                    {isNumeric(value) ? (
                      renderScoreBar(value, 10)
                    ) : Array.isArray(value) ? (
                      <ul className="mt-2">
                        {value.map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-700">
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p
                        style={{
                          marginTop: "5px",
                          color: "#374151",
                          fontSize: "14px",
                        }}
                      >
                        {value}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
      </div>
    );
  };

  const renderClientEvaluation = (data) => {
    if (!data) return null;

    const renderSectionContent = (sectionData) => {
      if (!sectionData || typeof sectionData !== "object") return null;

      return Object.entries(sectionData).map(([key, value]) => (
        <div key={key} className="eval-text-item">
          <strong>{key}:</strong>
          <p>{typeof value === "object" ? JSON.stringify(value) : value}</p>
        </div>
      ));
    };

    return (
      <div className="evaluation-content">
        {Object.entries(data).map(([sectionKey, sectionData]) => (
          <div key={sectionKey} className="eval-section">
            <h3 className="eval-section-title">{sectionKey}</h3>
            {renderSectionContent(sectionData)}
          </div>
        ))}
      </div>
    );
  };

  const renderSentimentAnalysis = (data) => {
    if (!data) {
      return null;
    }

    return (
      <div className="evaluation-content">
        {/* Overall Sentiment with Emoji */}
        {data["Overall Sentiment"] &&
          typeof data["Overall Sentiment"] === "object" && (
            <div className="eval-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="eval-section-title">Overall Sentiment</h3>
                <span className="text-5xl">
                  {data["Sentiment Emoji"] || "😐"}
                </span>
              </div>
              <div className="space-y-3">
                {data["Overall Sentiment"]["Classification"] && (
                  <div className="eval-text-item">
                    <strong>Classification:</strong>
                    <p
                      className="text-lg font-semibold"
                      style={{
                        color:
                          data["Overall Sentiment"]["Classification"] ===
                          "Positive"
                            ? "#27ae60"
                            : data["Overall Sentiment"]["Classification"] ===
                                "Negative"
                              ? "#e74c3c"
                              : "#f39c12",
                      }}
                    >
                      {data["Overall Sentiment"]["Classification"]}
                    </p>
                  </div>
                )}
                {data["Overall Sentiment"]["Sentiment Score"] !== undefined && (
                  <div className="eval-metric">
                    <span className="metric-label">Sentiment Score</span>
                    {renderScoreBar(
                      data["Overall Sentiment"]["Sentiment Score"],
                      10,
                    )}
                  </div>
                )}
                {data["Overall Sentiment"]["Confidence Score"] !==
                  undefined && (
                  <div className="eval-metric">
                    <span className="metric-label">Confidence Score</span>
                    {renderScoreBar(
                      data["Overall Sentiment"]["Confidence Score"],
                      100,
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Sentiment Distribution */}
        {data["Sentiment Distribution"] &&
          typeof data["Sentiment Distribution"] === "object" && (
            <div className="eval-section">
              <h3 className="eval-section-title">Sentiment Distribution</h3>
              <div className="space-y-3">
                {Object.entries(data["Sentiment Distribution"]).map(
                  ([emotion, value]) => (
                    <div key={emotion} className="eval-metric">
                      <span className="metric-label">{emotion}</span>
                      {renderScoreBar(value, 100)}
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

        {/* Engagement Level */}
        {data["Engagement Level"] &&
          typeof data["Engagement Level"] === "object" && (
            <div className="eval-section">
              <h3 className="eval-section-title">Engagement Level</h3>
              <div className="eval-text-item">
                <strong>Classification:</strong>
                <p
                  className="text-lg font-semibold"
                  style={{
                    color:
                      data["Engagement Level"]["Classification"] === "High"
                        ? "#27ae60"
                        : data["Engagement Level"]["Classification"] === "Low"
                          ? "#e74c3c"
                          : "#f39c12",
                  }}
                >
                  {data["Engagement Level"]["Classification"]}
                </p>
              </div>
              <div className="eval-text-item">
                <strong>Indicators of engagement:</strong>
                <p>{data["Engagement Level"]["Indicators of engagement"]}</p>
              </div>
            </div>
          )}

        {/* Sentiment Trajectory */}
        {data["Sentiment Trajectory"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Sentiment Trajectory</h3>
            <div className="space-y-2">
              <div className="eval-text-item">
                <strong>Starting Sentiment:</strong>
                <p>{data["Sentiment Trajectory"]["Starting sentiment"]}</p>
              </div>
              <div className="eval-text-item">
                <strong>Ending Sentiment:</strong>
                <p>{data["Sentiment Trajectory"]["Ending sentiment"]}</p>
              </div>
              <div className="eval-text-item">
                <strong>Evolution:</strong>
                <p>{data["Sentiment Trajectory"]["Evolution"]}</p>
              </div>
            </div>
          </div>
        )}

        {/* Key Emotional Moments */}
        {data["Key Emotional Moments"] &&
          Array.isArray(data["Key Emotional Moments"]) && (
            <div className="eval-section">
              <h3 className="eval-section-title">Key Emotional Moments</h3>
              <div className="space-y-4">
                {data["Key Emotional Moments"].map((moment, idx) => {
                  // Handle both capitalized and lowercase property names
                  const emotion = moment.Emotion || moment.emotion || "";
                  const quote = moment.Quote || moment.quote || "";
                  const timestamp = moment.Timestamp || moment.timestamp || "";
                  const significance =
                    moment.Significance || moment.significance || "";

                  return (
                    <div
                      key={idx}
                      className="p-4 rounded-lg border"
                      style={{
                        background:
                          "linear-gradient(135deg, rgb(250, 249, 255) 0%, rgb(245, 243, 255) 100%)",
                        borderColor: "rgb(233, 213, 255)",
                      }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span
                          className="text-sm font-semibold"
                          style={{ color: "#e04665" }}
                        >
                          {emotion}
                        </span>
                        <span className="text-xs text-gray-500">
                          {timestamp}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 italic mb-2">
                        "{quote}"
                      </p>
                      {significance && (
                        <p className="text-xs text-gray-600">{significance}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        {/* Positive Highlights */}
        {data["Positive Highlights"] && (
          <div className="eval-section">
            <h3 className="eval-section-title">Positive Highlights</h3>
            {data["Positive Highlights"][
              "What resonated well with the client"
            ] && (
              <div className="eval-text-item">
                <strong>What resonated well:</strong>
                <p>
                  {
                    data["Positive Highlights"][
                      "What resonated well with the client"
                    ]
                  }
                </p>
              </div>
            )}
            {data["Positive Highlights"][
              "Moments of strong positive sentiment"
            ] &&
              Array.isArray(
                data["Positive Highlights"][
                  "Moments of strong positive sentiment"
                ],
              ) && (
                <div className="eval-text-item">
                  <strong>Moments of strong positive sentiment:</strong>
                  <ul className="mt-2 space-y-1">
                    {data["Positive Highlights"][
                      "Moments of strong positive sentiment"
                    ].map((moment, idx) => (
                      <li key={idx} className="text-sm text-gray-700">
                        {moment}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
        )}

        {/* Red Flags */}
        {data["Red Flags"] && (
          <div className="eval-section" style={{ borderLeftColor: "#e74c3c" }}>
            <h3 className="eval-section-title" style={{ color: "#e74c3c" }}>
              Red Flags
            </h3>
            {Object.entries(data["Red Flags"]).map(([key, value]) => (
              <div key={key} className="eval-text-item">
                <strong>{key}:</strong>
                <p>{value}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderMessages = () => {
    const messages = [];

    chunks.forEach((chunk) => {
      const cleanText = formatChunkText(chunk.chunk_text);

      cleanText.split("\n").forEach((line) => {
        // Check if line contains a speaker pattern (Guest-N: or Name-N:)
        const speakerMatch = line.trim().match(/^([A-Za-z]+-\d+):\s*(.*)$/);
        if (speakerMatch) {
          const speaker = speakerMatch[1];
          const messageText = speakerMatch[2].trim();

          if (messageText) {
            messages.push({
              speaker,
              message: messageText,
              timestamp: chunk.timestamp,
              id: `${chunk.id}-${speaker}-${Date.now()}-${Math.random()}`,
            });
          }
        }
      });
    });

    return messages;
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#F5F3FF" }}
      >
        <div className="text-center">
          <div
            className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-200 mb-4"
            style={{ borderTopColor: "#e04665" }}
          ></div>
          <h4 className="text-lg text-gray-700">Loading meeting details...</h4>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-6" style={{ background: "#F5F3FF" }}>
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-100 text-gray-800 border border-gray-300 rounded-lg transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Meetings
        </button>
        <div className="max-w-2xl mx-auto bg-white border border-purple-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-start gap-3 mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-red-600 text-xl">⚠️</div>
            <div>
              <h3 className="text-red-600 font-semibold text-lg mb-1">
                Error Loading Meeting
              </h3>
              <p className="text-gray-700">{error}</p>
            </div>
          </div>
          <button
            onClick={fetchMeetingChunks}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm"
            style={{ backgroundColor: "#e04665" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#e63950")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#e04665")
            }
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const messages = renderMessages();

  return (
    <div className="min-h-screen" style={{ background: "#F5F3FF" }}>
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Back Button and Actions */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-100 text-gray-800 border border-gray-300 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Meetings
          </button>

          <div className="flex items-center gap-3">
            {/* View Full Analytics Button */}
            <button
              onClick={() => navigate(`/meeting-analytics/${meetingId}`)}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-all font-medium text-sm shadow-sm hover:shadow-md"
              style={{ backgroundColor: "#e04665" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#e63950")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#e04665")
              }
            >
              <TrendingUp className="w-4 h-4" />
              View Analytics
            </button>

            {/* View Evaluation Button */}
            {/* <button
              onClick={fetchEvaluation}
              disabled={loadingEvaluation}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white rounded-lg transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <BarChart3 className="w-4 h-4" />
              {loadingEvaluation ? "Loading..." : "View Evaluation"}
            </button> */}

            {/* View Sentiment Button */}
            {/* <button
              onClick={fetchSentiment}
              disabled={loadingSentiment}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SmilePlus className="w-4 h-4" />
              {loadingSentiment ? "Loading..." : "View Sentiment"}
            </button> */}

            {/* Group Selector */}
            <div className="relative group-selector-container">
              <button
                onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                disabled={updatingGroup}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium text-sm border ${
                  currentGroupId
                    ? "bg-white text-gray-800 border-gray-300"
                    : "bg-white/70 text-gray-600 border-gray-200"
                } ${updatingGroup ? "opacity-50 cursor-wait" : ""}`}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f9fafb")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = currentGroupId
                    ? "white"
                    : "rgba(255,255,255,0.7)")
                }
              >
                {currentGroupId ? (
                  <>
                    <Folder
                      size={16}
                      style={{
                        color:
                          groups.find((g) => g.id === currentGroupId)?.color ||
                          "#3b82f6",
                      }}
                    />
                    <span
                      style={{
                        color:
                          groups.find((g) => g.id === currentGroupId)?.color ||
                          "#fff",
                      }}
                    >
                      {groups.find((g) => g.id === currentGroupId)?.name ||
                        "Select Group"}
                    </span>
                  </>
                ) : (
                  <>
                    <FolderOpen size={16} />
                    <span>No Group</span>
                  </>
                )}
                <ChevronDown
                  size={16}
                  className={`transition-transform ${
                    showGroupDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showGroupDropdown && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl min-w-[240px] max-w-[300px] p-1 z-10 border border-purple-200 max-h-[400px] overflow-y-auto custom-scrollbar">
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Assign to Group
                  </div>

                  {/* No Group Option */}
                  <button
                    className={`w-full px-3 py-2 text-left text-sm font-medium rounded-md flex items-center gap-2 transition-colors ${
                      !currentGroupId ? "text-white" : "text-gray-600"
                    }`}
                    style={
                      !currentGroupId
                        ? {
                            backgroundColor: "rgba(224, 71, 101, 0.15)",
                            color: "#e04665",
                          }
                        : {}
                    }
                    onMouseEnter={(e) => {
                      if (currentGroupId)
                        e.currentTarget.style.backgroundColor =
                          "rgba(224, 71, 101, 0.05)";
                    }}
                    onMouseLeave={(e) => {
                      if (currentGroupId)
                        e.currentTarget.style.backgroundColor = "transparent";
                    }}
                    onClick={() => handleUpdateMeetingGroup(null)}
                  >
                    <FolderOpen size={16} className="flex-shrink-0" />
                    <span className="truncate">No Group</span>
                  </button>

                  {/* Group Options */}
                  {groups.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-500 italic">
                      No groups created yet
                    </div>
                  ) : (
                    groups.map((group) => (
                      <button
                        key={group.id}
                        className={`w-full px-3 py-2 text-left text-sm font-medium rounded-md flex items-center gap-2 transition-colors`}
                        style={
                          currentGroupId === group.id
                            ? { backgroundColor: "rgba(224, 71, 101, 0.15)" }
                            : {}
                        }
                        onMouseEnter={(e) => {
                          if (currentGroupId !== group.id)
                            e.currentTarget.style.backgroundColor =
                              "rgba(224, 71, 101, 0.05)";
                        }}
                        onMouseLeave={(e) => {
                          if (currentGroupId !== group.id)
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                        }}
                        onClick={() => handleUpdateMeetingGroup(group.id)}
                      >
                        <Folder
                          size={16}
                          className="flex-shrink-0"
                          style={{ color: group.color || "#8b5cf6" }}
                        />
                        <span
                          className="truncate"
                          style={{
                            color:
                              currentGroupId === group.id
                                ? group.color
                                : "#4b5563",
                          }}
                        >
                          {group.name}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            <button
              onClick={fetchMeetingChunks}
              className="px-4 py-2 text-white rounded-lg transition-all font-medium text-sm flex items-center gap-2 shadow-sm hover:shadow-md"
              style={{ backgroundColor: "#e04665" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#e63950")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#e04665")
              }
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-purple-200 rounded-xl p-6 text-center shadow-sm">
            <Users className="w-8 h-8 text-purple-600 mx-auto mb-3" />
            <div className="text-3xl font-bold text-gray-800 mb-2">
              {getUniqueParticipants(chunks)}
            </div>
            <div className="text-sm text-gray-600">Participants</div>
          </div>
          <div className="bg-white border border-purple-200 rounded-xl p-6 text-center shadow-sm">
            <MessageSquare className="w-8 h-8 text-purple-600 mx-auto mb-3" />
            <div className="text-3xl font-bold text-gray-800 mb-2">
              {messages.length}
            </div>
            <div className="text-sm text-gray-600">Messages</div>
          </div>
          <div className="bg-white border border-purple-200 rounded-xl p-6 text-center shadow-sm">
            <Calendar className="w-8 h-8 text-purple-600 mx-auto mb-3" />
            <div className="text-3xl font-bold text-gray-800 mb-2">
              {chunks.length > 0
                ? new Date(chunks[0].timestamp).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : "N/A"}
            </div>
            <div className="text-sm text-gray-600">Date</div>
          </div>
        </div>

        {/* Summary and Action Items */}
        {(summary || actionItems) && (
          <div className="space-y-6 mb-8">
            {summary && (
              <div className="bg-white border border-purple-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Meeting Summary
                  </h3>
                  <button
                    onClick={() => copyToClipboard(summary, "Summary")}
                    className="px-3 py-1 rounded text-xs transition-colors flex items-center gap-2 text-white"
                    style={{ backgroundColor: "#e04665" }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#e63950")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "#e04665")
                    }
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </button>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {summary}
                </p>
              </div>
            )}
            {actionItems && (
              <div className="bg-white border border-purple-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Action Items
                  </h3>
                  <button
                    onClick={() => copyToClipboard(actionItems, "Action Items")}
                    className="px-3 py-1 rounded text-xs transition-colors flex items-center gap-2 text-white"
                    style={{ backgroundColor: "#e04665" }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#e63950")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "#e04665")
                    }
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </button>
                </div>
                <div
                  dangerouslySetInnerHTML={{ __html: actionItems }}
                  className="text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none"
                />
              </div>
            )}
          </div>
        )}

        {/* Conversation Transcript */}
        <div className="bg-white border border-purple-200 rounded-xl p-6 shadow-sm mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Conversation Transcript
            </h3>
            {chunks.length > 0 && (
              <div className="px-3 py-1 bg-purple-100 text-purple-700 rounded text-xs flex items-center gap-2">
                <Clock className="w-3 h-3" />
                {formatTimestamp(chunks[0].timestamp)}
              </div>
            )}
          </div>

          {messages.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h4 className="text-gray-800 font-semibold mb-2">
                No Conversation Found
              </h4>
              <p className="text-gray-600 text-sm">
                This meeting doesn't have any recorded conversation yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[600px] overflow-y-auto bg-white/50 rounded-lg p-4 border border-gray-200">
              {messages.map((msg, index) => (
                <div
                  key={msg.id || index}
                  className={`flex gap-3 p-4 rounded-lg ${
                    msg.speaker.includes("1")
                      ? "bg-purple-50 border border-purple-300"
                      : "bg-white border border-purple-200"
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.speaker.includes("1")
                        ? "bg-purple-200"
                        : "bg-emerald-100"
                    }`}
                  >
                    <Users
                      className={`w-5 h-5 ${
                        msg.speaker.includes("1")
                          ? "text-purple-700"
                          : "text-emerald-600"
                      }`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded ${
                          msg.speaker.includes("1")
                            ? "bg-purple-200 text-purple-800"
                            : "bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {msg.speaker}
                      </span>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(msg.timestamp).toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <p className="text-gray-800 text-sm leading-relaxed font-medium">
                      {msg.message}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* AI Suggestions Section */}
        {suggestions.length > 0 && (
          <div className="bg-white border border-purple-200 rounded-xl p-6 shadow-sm mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                AI Suggestions
              </h3>
              <div className="px-3 py-1 bg-purple-100 text-purple-700 rounded text-xs flex items-center gap-2">
                {suggestions.length} suggestion
                {suggestions.length !== 1 ? "s" : ""}
              </div>
            </div>

            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  className="p-4 border border-purple-200 rounded-lg bg-gradient-to-br from-purple-50 to-blue-50"
                >
                  {/* Question */}
                  {suggestion.question && (
                    <div className="mb-3 p-3 bg-white/70 rounded border-l-3 border-l-purple-500">
                      <strong className="text-xs text-gray-600">
                        Question:
                      </strong>
                      <p className="text-sm text-gray-800 mt-1">
                        {suggestion.question}
                      </p>
                    </div>
                  )}

                  {/* Suggestion */}
                  <div className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">
                    {suggestion.suggestion}
                  </div>

                  {/* Timestamp */}
                  {suggestion.timestamp && (
                    <div className="text-xs text-gray-500 mb-3">
                      {new Date(suggestion.timestamp).toLocaleString()}
                    </div>
                  )}

                  {/* Feedback Section */}
                  <div className="pt-3 border-t border-purple-200">
                    {/* Show existing feedback or allow new feedback */}
                    {suggestion.rating || suggestion.feedback ? (
                      <div className="space-y-2">
                        {suggestion.rating && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600">
                              Your rating:
                            </span>
                            <div className="flex gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  size={14}
                                  fill={
                                    star <= suggestion.rating
                                      ? "#fbbf24"
                                      : "none"
                                  }
                                  stroke={
                                    star <= suggestion.rating
                                      ? "#fbbf24"
                                      : "#d1d5db"
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {suggestion.feedback && (
                          <div className="p-2 bg-white/70 rounded text-xs">
                            <strong>Your feedback:</strong>{" "}
                            {suggestion.feedback}
                          </div>
                        )}
                        {suggestion.feedback_timestamp && (
                          <div className="text-xs text-gray-500">
                            Submitted:{" "}
                            {new Date(
                              suggestion.feedback_timestamp,
                            ).toLocaleString()}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {/* Star Rating */}
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs text-gray-600 font-medium">
                            Rate this suggestion:
                          </span>
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((star) => {
                              const currentRating =
                                feedbackStates[suggestion.id]?.rating || 0;
                              const isSelected = star <= currentRating;
                              return (
                                <Star
                                  key={star}
                                  size={16}
                                  fill={isSelected ? "#fbbf24" : "none"}
                                  stroke={isSelected ? "#fbbf24" : "#9ca3af"}
                                  style={{
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                  }}
                                  onClick={() =>
                                    handleStarClick(suggestion.id, star)
                                  }
                                />
                              );
                            })}
                          </div>
                          {feedbackStates[suggestion.id]?.submitted && (
                            <span className="text-xs text-green-600 font-medium">
                              ✓ Submitted
                            </span>
                          )}
                        </div>

                        {/* Feedback Text Box */}
                        {feedbackStates[suggestion.id]?.showFeedbackBox && (
                          <div className="mt-3 space-y-2">
                            <textarea
                              placeholder="Share your thoughts... (optional)"
                              value={
                                feedbackStates[suggestion.id]?.feedback || ""
                              }
                              onChange={(e) =>
                                handleFeedbackChange(
                                  suggestion.id,
                                  e.target.value,
                                )
                              }
                              className="w-full p-2 text-sm border border-gray-300 rounded focus:border-purple-500 focus:outline-none resize-vertical"
                              rows="3"
                            />
                            <button
                              onClick={() =>
                                submitFeedback(
                                  suggestion.id,
                                  feedbackStates[suggestion.id]?.rating,
                                  feedbackStates[suggestion.id]?.feedback,
                                )
                              }
                              disabled={submittingFeedback[suggestion.id]}
                              className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                            >
                              <Send size={12} />
                              {submittingFeedback[suggestion.id]
                                ? "Submitting..."
                                : "Submit Feedback"}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Evaluation Modal */}
      {showEvaluationModal && evaluationData && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-5"
          onClick={() => setShowEvaluationModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-purple-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              className="p-6 flex items-center justify-between"
              style={{ background: "#e04665" }}
            >
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <BarChart3 className="w-7 h-7" />
                Meeting Evaluation
              </h2>
              <button
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-all hover:rotate-90"
                onClick={() => setShowEvaluationModal(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 bg-gray-50">
              <button
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-all relative ${
                  evaluationType === "sales"
                    ? "text-gray-900"
                    : "text-gray-600 hover:text-gray-800"
                }`}
                onClick={() => setEvaluationType("sales")}
              >
                Sales Evaluation
                {evaluationType === "sales" && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-0.5"
                    style={{ background: "#e04665" }}
                  ></div>
                )}
              </button>
              <button
                className={`flex-1 px-6 py-4 text-sm font-semibold transition-all relative ${
                  evaluationType === "client"
                    ? "text-gray-900"
                    : "text-gray-600 hover:text-gray-800"
                }`}
                onClick={() => setEvaluationType("client")}
              >
                Client Evaluation
                {evaluationType === "client" && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-0.5"
                    style={{ background: "#e04665" }}
                  ></div>
                )}
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingEvaluation ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div
                    className="w-12 h-12 border-4 border-purple-200 rounded-full animate-spin mb-4"
                    style={{ borderTopColor: "#e04665" }}
                  ></div>
                  <p className="text-gray-600">Loading evaluation data...</p>
                </div>
              ) : evaluationType === "sales" ? (
                renderSalesEvaluation(evaluationData?.sales_evaluation)
              ) : (
                renderClientEvaluation(evaluationData?.client_evaluation)
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .evaluation-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .eval-section {
          background: linear-gradient(
            to bottom right,
            rgb(250, 249, 255),
            rgb(245, 243, 255)
          );
          padding: 1.5rem;
          border-radius: 0.75rem;
          border-left: 4px solid #e04665;
          border: 1px solid rgb(233, 213, 255);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .eval-section-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #e04665;
          margin: 0 0 1.25rem 0;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid rgba(224, 71, 101, 0.2);
        }

        .eval-subsection-title {
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 0.5rem 0;
          display: block;
        }

        .eval-metric {
          margin-bottom: 1.25rem;
        }

        .metric-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: rgb(107, 114, 128);
          display: block;
          margin-bottom: 0.5rem;
        }

        .eval-text-item {
          margin-bottom: 1rem;
        }

        .eval-text-item strong {
          color: rgb(55, 65, 81);
          font-weight: 600;
          display: block;
          margin-bottom: 0.25rem;
          font-size: 0.875rem;
        }

        .eval-text-item p {
          color: rgb(75, 85, 99);
          line-height: 1.6;
          margin: 0;
          font-size: 0.875rem;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgb(243, 244, 246);
          border-radius: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgb(209, 213, 219);
          border-radius: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #e04665;
        }
      `}</style>

      {/* Sentiment Modal */}
      {showSentimentModal && sentimentData && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowSentimentModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-11/12 max-w-5xl max-h-[90vh] overflow-hidden border border-purple-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between p-6 border-b border-purple-200"
              style={{ background: "#e04665" }}
            >
              <div className="flex items-center gap-3">
                <SmilePlus className="w-6 h-6 text-white" />
                <h3 className="text-2xl font-bold text-white">
                  Customer Sentiment Analysis
                </h3>
              </div>
              <button
                onClick={() => setShowSentimentModal(false)}
                className="text-white hover:text-gray-100 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)] custom-scrollbar">
              {loadingSentiment ? (
                <div className="text-center py-12">
                  <div
                    className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto"
                    style={{ borderColor: "#e04665" }}
                  ></div>
                  <p className="text-gray-600 mt-4">
                    Loading sentiment analysis...
                  </p>
                </div>
              ) : sentimentError ? (
                <div className="text-center py-12">
                  <p className="text-red-600">{sentimentError}</p>
                </div>
              ) : (
                renderSentimentAnalysis(sentimentData)
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingDetails;
